import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Remembers where each scrollable area of a page was and puts it back when the
 * user presses the browser's Back button.
 *
 * Lists used to open their rows in a new tab precisely so that the list itself
 * — its filters, its scroll — stayed untouched. Now that everything navigates
 * in place, that has to be restored explicitly. React Router ships
 * <ScrollRestoration/> only for data routers (this app mounts a plain
 * <BrowserRouter/>), and the app barely scrolls the window at all: <main>
 * scrolls, and DataTable scrolls its own body. So the elements to track are the
 * ones marked with `data-scroll-restore`, plus the window as a fallback.
 */

const SCROLL_ATTR = "data-scroll-restore";
const STORAGE_KEY = "erp:scroll-positions";
/** Plenty for a session's worth of back/forward without growing forever. */
const MAX_ENTRIES = 40;
/** How long to keep trying — long enough for a list to fetch and render. */
const MAX_RESTORE_MS = 3000;
/** Sub-pixel scroll offsets are never worth another frame. */
const TOLERANCE = 2;

type Snapshot = Record<string, number>;
type Target = { key: string; element: Element | null };

const collectTargets = (): Target[] => {
  const seen: Record<string, number> = {};
  const targets: Target[] = [{ key: "window", element: null }];
  const elements = document.querySelectorAll(`[${SCROLL_ATTR}]`);
  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    const name = element.getAttribute(SCROLL_ATTR) || "unnamed";
    const ordinal = seen[name] ?? 0;
    seen[name] = ordinal + 1;
    // Several tables on one page keep distinct keys as long as they render in
    // the same order, which they do once the page is back to the same data.
    targets.push({ key: `${name}#${ordinal}`, element });
  }
  return targets;
};

const getScrollTop = (element: Element | null) =>
  element ? element.scrollTop : window.scrollY;

const setScrollTop = (element: Element | null, top: number) => {
  if (element) {
    element.scrollTop = top;
  } else {
    window.scrollTo(0, top);
  }
};

const readStored = (): Record<string, Snapshot> => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeStored = (positions: Record<string, Snapshot>) => {
  try {
    const keys = Object.keys(positions);
    const kept = keys.slice(Math.max(keys.length - MAX_ENTRIES, 0));
    const trimmed: Record<string, Snapshot> = {};
    kept.forEach((key) => {
      trimmed[key] = positions[key];
    });
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    /* a full or unavailable sessionStorage must not break navigation */
  }
};

/**
 * Keeps nudging every remembered scroll container back to its offset until the
 * page has rendered enough content to hold it — a list that is still fetching
 * is one row tall, and a single attempt would silently land at the top.
 */
const restoreSnapshot = (snapshot: Snapshot): (() => void) => {
  const savedKeys = Object.keys(snapshot);

  const interrupts: string[] = ["wheel", "touchstart", "keydown", "mousedown"];
  const deadline = performance.now() + MAX_RESTORE_MS;
  let frame = 0;
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
  };

  const cleanup = () => {
    interrupts.forEach((name) => window.removeEventListener(name, cancel));
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
  };

  const step = () => {
    frame = 0;
    if (cancelled) {
      cleanup();
      return;
    }
    const targets = collectTargets();
    const present: Record<string, true> = {};
    targets.forEach(({ key }) => {
      present[key] = true;
    });
    // A container that has not mounted yet means the page is still loading.
    let settled = savedKeys.every((key) => present[key]);
    targets.forEach(({ key, element }) => {
      // Absent from the snapshot means it was at the top. <main> outlives the
      // page change, so skipping it here would bring a list back sitting at
      // the offset the detail page left behind.
      const top = snapshot[key] ?? 0;
      if (Math.abs(getScrollTop(element) - top) <= TOLERANCE) return;
      setScrollTop(element, top);
      if (getScrollTop(element) + TOLERANCE < top) settled = false;
    });
    if (!settled && performance.now() < deadline) {
      frame = window.requestAnimationFrame(step);
    } else {
      cleanup();
    }
  };

  interrupts.forEach((name) =>
    window.addEventListener(name, cancel, { passive: true })
  );
  frame = window.requestAnimationFrame(step);
  return cleanup;
};

export function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positionsRef = useRef<Record<string, Snapshot> | null>(null);
  const entryKeyRef = useRef<string>(location.key);
  const pathnameRef = useRef<string | null>(null);

  if (positionsRef.current === null) {
    positionsRef.current = readStored();
  }

  useLayoutEffect(() => {
    let frame = 0;

    const snapshot = () => {
      frame = 0;
      const positions = positionsRef.current;
      if (!positions) return;
      const next: Snapshot = {};
      collectTargets().forEach(({ key, element }) => {
        const top = getScrollTop(element);
        if (top > 0) next[key] = top;
      });
      positions[entryKeyRef.current] = next;
    };

    // `scroll` does not bubble, so the listener has to capture instead.
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(snapshot);
    };

    const persist = () => {
      if (positionsRef.current) writeStored(positionsRef.current);
    };

    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("pagehide", persist);
    return () => {
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("pagehide", persist);
      if (frame) window.cancelAnimationFrame(frame);
      persist();
    };
  }, []);

  // A layout effect, not an effect: the entry key has to move to the new page
  // before the browser dispatches the scroll events that unmounting the old
  // page produces, or those zeroes get written over the position we just left.
  useLayoutEffect(() => {
    const previousPathname = pathnameRef.current;
    const positions = positionsRef.current ?? {};
    entryKeyRef.current = location.key;
    pathnameRef.current = location.pathname;

    // First render: the browser has already placed a reloaded page itself.
    if (previousPathname === null) return undefined;

    if (navigationType === "POP") {
      return restoreSnapshot(positions[location.key] || {});
    }

    if (previousPathname !== location.pathname) {
      // Following a link opens the target at the top, the way a new tab did.
      // Changing only the query string (filters, tabs) must not move anything.
      collectTargets().forEach(({ element }) => setScrollTop(element, 0));
      positions[location.key] = {};
    }
    return undefined;
  }, [location.key, location.pathname, navigationType]);

  return null;
}

export default ScrollRestoration;
